import React, { useMemo, useState } from 'react';
import { useProfileParams } from '../../../../../../utils/common';
import KPITile from '../../../../../common/KPITile/KPITile';
import KPIDetailTile from '../../../../../common/KPIDetailTile/KPIDetailTile';
import DonutChart, { DonutChartSegment } from '../../../../../common/DonutChart/DonutChart';
import InsightAI from '../../../../../common/InsightAI/InsightAI';
import { useTranslation } from 'react-i18next';
import ViewModeSwitcher, {
  ViewMode,
} from '../../../../../common/ViewModeSwitcher/ViewModeSwitcher';
import Skeleton from '../../../../../common/Skeleton/Skeleton';
import sharedStyles from "../../../../ABOGrowthCompanion/common/SharedAGCStyles.module.scss";
import { useGetBusinessPerformanceDetailsQuery } from '../../../../../../redux/services/businessPerformaceAPI';
import KPITableWithPills from '../../../common/KPITableWithPills/KPITableWithPills';

interface SummaryProp {
  summary?: any;
  loading?: boolean;
}

export const CategoryMix: React.FC<SummaryProp> = ({ summary, loading = false }) => {
  // Display API-provided segments when available; otherwise show nothing (0 fallback is implicit per segment values)
  const segments: DonutChartSegment[] = Array.isArray(summary?.segments) ? summary!.segments : [];
  const { t } = useTranslation();

  return (
    <KPITile
      title={t('category_mix')}
      dataTestId="category-mix-tile"
      loading={loading}
    >
      <div className={sharedStyles.tile_content}>
        <DonutChart
          size={125}
          strokeWidth={20}
          legendGap={24}
          segments={segments}
          showLegend
          isPortfolio
        />
      </div>
    </KPITile>
  );
};

export const CategoryMixDetails: React.FC<{
  kpiCode?: string;
  onClose?: () => void;
}> = ({ kpiCode = 'category', onClose }) => {
  type View = 'perfYear' | 'monthly' | 'yearly';
  const { affiliateCode, aboNumber } = useProfileParams();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('left');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('perfYear');

  const isLoadingAiSummary = false;

  const { data: detailsResponse } = useGetBusinessPerformanceDetailsQuery(
    { affiliateCode, aboNumber, payload: { kpis: [{ code: kpiCode }] } },
    { skip: !onClose },
  );

  const kpiData = useMemo(() => {
    if (detailsResponse?.body?.kpis) {
      return detailsResponse.body.kpis.find((k: any) => k.kpiCode === kpiCode);
    }
    return null;
  }, [detailsResponse, kpiCode]);

  const ytdViewData = kpiData?.detail?.ytdView?.chart.find(
    (c: { chartType: string; chartDetail: any }) => c.chartType === 'table',
  )?.chartDetail?.tableDetail;

  const viewMap: Record<View, JSX.Element> = {
    perfYear: (
      <KPITableWithPills
        data={ytdViewData}
        affiliateCode={affiliateCode}
        applyGrouping={true}
        title="categorymix.py_subtitle"
        subtitle="categorymix.sales_performance_description"
      />
    ),
    monthly: <></>, //<BusinessLineMonthlyView tableData={tableData} kpiCode={"business-line"} />,
    yearly: <></>, //<BusinessLineYearlyView viewMode={viewMode}
  };

  const perfYear = 'PY 25'; // TODO Change to real performance year from API
  const periodItems = useMemo(
    () => [
      { code: 'perfYear', label: perfYear },
      { code: 'monthly', label: t('monthly_view') },
      { code: 'yearly', label: t('yearly_view') },
    ],
    [t],
  );

    const headerContent = (
      <div className={sharedStyles.header_content}>
        <div className={sharedStyles.tile_header}>
          <div className={sharedStyles.title}>{t('categorymix.title')}</div>
          <div className={sharedStyles.desc}>{t('categorymix.description')}</div>
        </div>
          <ViewModeSwitcher
            mode={viewMode}
            onChangeMode={setViewMode}
            disabled={false}
            showSwitch={selectedPeriod === 'yearly'}
            selectedTimeRangeCode={selectedPeriod}
            onSelectTimeRange={setSelectedPeriod}
            timeRanges={periodItems}
          />
        {selectedPeriod !== 'perfYear' && (
          <div className={sharedStyles.tile_header}>
            <div className={sharedStyles.title} style={{ fontSize: '16px' }}>
              {selectedPeriod === 'yearly'
                ? t('categorymix.categoryMixYearlySubtitle')
                : t('categorymix.categoryMixMonthlySubtitle')}
            </div>
            <div className={sharedStyles.desc} style={{ fontSize: '12px' }}>
              {t('categorymix.categoryMixMonthlySubHeader')}
            </div>
          </div>
        )}
      </div>
    );

  const leftContent = viewMap[selectedPeriod as View] ?? null;

  // const leftContent = (
  //   <div className={styles.left_content_wrapper}>
  //     <div className={styles.dropdown_wrapper}>
  //       <PeriodDropdown
  //         options={periodOptions}
  //         selectedValue={selectedPeriod}
  //         onSelect={(value) => setSelectedPeriod(value)}
  //       />
  //     </div>
  //     {selectedPeriod === "py25" ? (
  //       <CategoryPerformanceView viewType="chart" showTabs={true} />
  //     ) : selectedPeriod === "monthly" ? (
  //       <CategoryPerformanceView
  //         viewType="table"
  //         data={businessLineMonthly}
  //         title={t("categorymix.monthly_com_title")}
  //         description={t("categorymix.sales_performance_description")}
  //       />
  //     ) : (
  //       <div>Placeholder</div>
  //     )}
  //   </div>
  // );

  const rightContent = isLoadingAiSummary ? (
    <Skeleton height="200px" />
  ) : (
    <InsightAI
      updateDate=""
      content={""}
      title={t('ai_summary')}
      showDropdown={false}
      isDropdownEnabled={true}
      flowerIconSize={24}
      titleSize={14}
    />
  );

  return (
    <KPIDetailTile
      headerContent={headerContent}
      leftContent={leftContent}
      rightContent={rightContent}
      onClose={onClose!}
      dataTestId="category-mix-detail"
    />
  );
};

export default CategoryMix;
